/* eslint-disable @typescript-eslint/no-empty-function */
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { isValidEmail } from 'utils/common'
import useThriveAuth from 'hooks/useThriveAuth'
import { sessionStorageKeys } from 'constants/common'
import { wpFormLandingPageUrl } from 'app/ProviderSearch/constants'
import { enhanceUrlWithMarketingParams } from 'lib/marketingParams'
import {
  RouterFilters,
  GroupedFiltersMapping,
  leadGenFilterKeyMapping,
  LeadGenScenarios,
  ForwardingFilters,
} from './constants'

export { RouterFilters, leadGenFilterKeyMapping, LeadGenScenarios }
export type { ForwardingFilters }

interface LeadGenContextValue {
  scenario: LeadGenScenarios
  forwardingFilters: ForwardingFilters
  inputValue: string
  isValidEmailCheck: boolean
  onChangeInput: (value: string) => void
  onClickButton: () => void
  setScenario: (scenario: LeadGenScenarios) => void
  setProviderId: (providerId: string | undefined) => void
}

const initialContextValue: LeadGenContextValue = {
  scenario: LeadGenScenarios.SEARCH_NO_SUPPLY,
  forwardingFilters: {},
  inputValue: '',
  isValidEmailCheck: false,
  onChangeInput: () => {},
  onClickButton: () => {},
  setScenario: () => {},
  setProviderId: () => {},
}

export const LeadGenContext = createContext<LeadGenContextValue>(
  initialContextValue
)

interface LeadGenProviderProps {
  children: React.ReactNode
  groupedFilters: ProviderFilterLookup | GroupedProviderFilter
  initialScenario?: LeadGenScenarios
  initialProviderId?: string
}

export const LeadGenProvider: React.FC<LeadGenProviderProps> = ({
  children,
  groupedFilters,
  initialScenario = LeadGenScenarios.SEARCH_NO_SUPPLY,
  initialProviderId,
}) => {
  const [inputValue, setInputValue] = useState('')
  const [scenario, setScenario] = useState<LeadGenScenarios>(initialScenario)
  const [providerId, setProviderId] = useState<string | undefined>(
    initialProviderId
  )

  const router = useRouter()
  const route = usePathname()
  const searchParams = useSearchParams()

  const isValidEmailCheck = isValidEmail(inputValue)

  const {
    user: { email, gender },
    isAuthenticated,
    isLoading,
  } = useThriveAuth()

  const forwardingFilters: ForwardingFilters = useMemo(() => {
    const filters: ForwardingFilters = {
      [leadGenFilterKeyMapping.LEAD_GEN_TYPE]: scenario,
    }

    for (const filter of Object.values(RouterFilters)) {
      let paramValue: string | string[] | null = searchParams.get(filter)

      if (!paramValue) {
        const arrayValues = searchParams.getAll(`${filter}[]`)

        if (arrayValues.length > 0) {
          paramValue = arrayValues.length === 1 ? arrayValues[0] : arrayValues
        }
      }

      if (paramValue) {
        if (GroupedFiltersMapping[filter] !== undefined) {
          filters[leadGenFilterKeyMapping[filter]] = groupedFilters[
            GroupedFiltersMapping[filter]
          ]?.find<ProviderFilter>((f) => {
            if (paramValue instanceof Array) {
              return paramValue.some((p) => p === f.id)
            }

            return f.id === paramValue
          })?.value
        } else {
          filters[leadGenFilterKeyMapping[filter]] = paramValue
        }
      }
    }

    if (inputValue) {
      filters.Email = inputValue
    }

    if (providerId) {
      filters.Provider_ID__c = providerId
    }

    if (!isLoading && isAuthenticated && gender) {
      filters[leadGenFilterKeyMapping.USER_GENDER] = gender
    }

    filters['Form_URL__c'] = route

    return filters
  }, [
    scenario,
    searchParams,
    inputValue,
    providerId,
    gender,
    isAuthenticated,
    isLoading,
    route,
    groupedFilters,
  ])

  const onChangeInput = (value: string) => setInputValue(value)

  const onClickButton = () => {
    router.push(enhanceUrlWithMarketingParams(wpFormLandingPageUrl))
  }

  useEffect(() => {
    if (Object.values(forwardingFilters).length) {
      sessionStorage.setItem(
        sessionStorageKeys.LEAD_GEN_FILTERS,
        JSON.stringify(forwardingFilters)
      )
    }
  }, [forwardingFilters])

  useEffect(() => {
    if (email) {
      setInputValue((prev) => (prev !== email ? email : prev))
    }
  }, [email])

  useEffect(() => {
    setScenario(initialScenario)
  }, [initialScenario])

  const contextValue: LeadGenContextValue = {
    scenario,
    forwardingFilters,
    inputValue,
    isValidEmailCheck,
    onChangeInput,
    onClickButton,
    setScenario,
    setProviderId,
  }

  return (
    <LeadGenContext.Provider value={contextValue}>
      {children}
    </LeadGenContext.Provider>
  )
}

export const useLeadGenContext = (): LeadGenContextValue => {
  const context = useContext(LeadGenContext)

  if (
    process.env.NODE_ENV === 'development' &&
    context === initialContextValue
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      'useLeadGenContext must be used within a LeadGenProvider. ' +
        'Returning default values.'
    )
  }

  return context
}

export default LeadGenContext
